import URL from 'node:url';

import { TrackInspirationPageViewOptions } from '@oneflare/web-analytics';
import { withUserAgent } from 'next-useragent';
import { useMemo } from 'react';

import { PageViewType, useTrackPageView } from 'lib/analytics/trackPageViews';
import { addApolloState } from 'lib/apollo/withApolloClient';
import { handleMultipleServerQueries } from 'lib/page-data-oneflare/helpers/multipleQueryHelper';
import ApolloRun from 'lib/utils/apolloRun';
import { GET_ALL_POSTS } from 'queries/oneflare.com.au/inspiration';
import { GET_WP_CATEGORY } from 'queries/oneflare.com.au/wpGetCategory';
import { GET_WP_CATEGORY_WITH_PARENT } from 'queries/oneflare.com.au/wpGetCategoryWithParent';
import { GET_WP_CATEGORY_WITH_PARENT_AND_SLUG } from 'queries/oneflare.com.au/wpGetCategoryWithParentAndSlug';
import { GET_COST_GUIDES_FOR_HOMEPAGE } from 'queries/shared/costGuides';
import { EXCLUDE_WP_CATEGORIES, INSPIRATION_WP_CATEGORY_ID } from 'shared/utils/constants';
import Category from 'src/pages/oneflare.com.au/Inspiration/Category';
import type { UserAgentModel } from 'types/oneflare.com.au/helpers';
import type { IWpInspirationDataModel } from 'types/oneflare.com.au/inspiration';
import type { IWpGetCategoryDataModel } from 'types/shared';

type Props = {
  ua?: UserAgentModel;
  params?: {
    slug: string
  };
  pageUrl?: string;
  getCategoryPrefix?: Omit<IWpGetCategoryDataModel, 'slug'>;
  subCategoryData?: Array<IWpGetCategoryDataModel>;
  wpData?: IWpInspirationDataModel
};

function Inspiration(props: Partial<Props>) {
  const { ua, params: inspirationProps } = props;

  const isBot = useMemo(() => {
    return ua?.isBot ?? false;
  }, [ua]);

  const params = {
    ...inspirationProps,
    staticRender: isBot
  };

  useTrackPageView(PageViewType.Inspiration, null, {
    inspirationCategory: params.slug
  } as TrackInspirationPageViewOptions);

  return <Category {...props} params={params} />;
}

export async function getStaticPaths() {
  const prodPaths = [
    { params: { category: 'indoor-projects' } },
    { params: { category: 'outdoor-projects' } },
    { params: { category: 'lifestyle' } },
    { params: { category: 'professional-services' } },
    { params: { category: 'weddings' } }
  ];
  return {
    paths: process.env.ENVIRONMENT === 'production' ? prodPaths : [],
    fallback: 'blocking'
  };
}

export async function getStaticProps(ctx) {
  if (process.env.DOMAIN !== 'oneflare') {
    return {
      notFound: true
    };
  }

  const defaultQueryConfig = {
    limit: 4,
    exclude: 0,
    search: '',
    order: 'desc',
    orderby: 'date',
    categoriesExclude: EXCLUDE_WP_CATEGORIES,
    categories: '',
    tags: '',
    sticky: false
  };

  const apolloRun = new ApolloRun(ctx);

  const { params } = ctx;
  const { category } = params;

  const pageUrl = `/inspiration${(category && `/${category}`) || ''}/`;
  const [topLevelCategoryData] = await apolloRun.query(GET_WP_CATEGORY_WITH_PARENT, {
    parent: INSPIRATION_WP_CATEGORY_ID,
    orderby: 'description'
  });

  // Handle all routing for Inspiration
  // NOTE: Category slugs take precedence over article slugs
  const [categoryData, categoryError] = await apolloRun.query(
    GET_WP_CATEGORY,
    { slug: category }
  );

  if (!categoryData?.getCategory[0]?.link || categoryError) {
    return {
      notFound: true
    };
  }

  const wpCategoryUri = URL.parse(categoryData?.getCategory[0]?.link);

  if (wpCategoryUri.pathname.replace('category/', '') !== pageUrl) {
    return {
      notFound: true
    };
  }

  const [categoryWithParentAndSlugData, categoryWithParentAndSlugError] = await apolloRun.query(
    GET_WP_CATEGORY_WITH_PARENT_AND_SLUG,
    {
      slug: category,
      parent: INSPIRATION_WP_CATEGORY_ID
    }
  );

  const { getCategory: [getCategoryPrefix] } = categoryWithParentAndSlugData;

  if (!getCategoryPrefix || categoryWithParentAndSlugError) {
    return {
      notFound: true
    };
  }

  const [categoryWithParentData] = await apolloRun.query(
    GET_WP_CATEGORY_WITH_PARENT,
    {
      parent: getCategoryPrefix.categoryId,
      orderby: 'name'
    }
  );

  const subCategoryData = categoryWithParentData.getCategory;
  const subCategoryArticleQueries = subCategoryData.reduce((acc, curr) => {
    const mergeQueryConfig = {
      ...defaultQueryConfig,
      ...{ categories: curr.categoryId }
    };
    const query = {
      query: GET_ALL_POSTS,
      variables: {
        limit: mergeQueryConfig.limit,
        exclude: mergeQueryConfig.exclude,
        categories: mergeQueryConfig.categories,
        categoriesExclude: mergeQueryConfig.categoriesExclude,
        search: '',
        order: mergeQueryConfig.order,
        orderby: 'date',
        sticky: mergeQueryConfig.sticky
      },
      noKey: true
    };
    acc['getAllPost' + curr.categoryId] = query;
    return acc;
  }, {});

  const subCategoryArticlesData = await handleMultipleServerQueries({
    worker: apolloRun,
    queries: subCategoryArticleQueries,
    disableOtel: true
  });
  await apolloRun.query(GET_COST_GUIDES_FOR_HOMEPAGE);
  return addApolloState(apolloRun.apolloClient, {
    props: {
      params: {
        slug: category
      },
      pageUrl: pageUrl.endsWith('/') ? pageUrl.slice(0, -1) : pageUrl,
      getCategoryPrefix,
      subCategoryData,
      getAllPosts: Object.values(subCategoryArticlesData),
      wpData: {
        topLevelCategoryData: topLevelCategoryData?.getCategory,
        ...(categoryData && { categoryData: categoryData?.getCategory[0] })
      }
    },
    revalidate: 3600
  });
}

export default (withUserAgent(Inspiration));
